import React, { useCallback, useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import editIcon from '../../../../assests/icons/edit_icon.png';
import eraserIcon from '../../../../assests/icons/eraser_icon.png';
import { uploadSignature } from './signatureApi';
import { useSignaturePad } from './useSignaturePad';
import './SignatureCaptureScreen.css';

export interface SignatureCaptureScreenProps {
  email: string;
  onBack: () => void;
  onComplete: () => void;
  /** When true, show the legal-name signature warning before drawing (first sign and redo). */
  showEntryWarning?: boolean;
}

const COPYRIGHT = '© 2026 株式会社西行土木. All Rights Reserved';

const SignatureCaptureScreen: React.FC<SignatureCaptureScreenProps> = ({
  email,
  onBack,
  onComplete,
  showEntryWarning = true,
}) => {
  const { t } = useLanguage();
  const pad = useSignaturePad();
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [entryWarningOpen, setEntryWarningOpen] = useState(showEntryWarning);

  const handleConfirm = useCallback(async () => {
    if (!pad.hasStrokes) {
      setErrorKey('empty');
      return;
    }
    setErrorKey(null);
    setSubmitting(true);
    try {
      const blob = await pad.exportPngBlob();
      if (!blob) {
        setErrorKey('empty');
        return;
      }
      await uploadSignature(email, blob);
      onComplete();
    } catch {
      setErrorKey('upload');
    } finally {
      setSubmitting(false);
    }
  }, [email, onComplete, pad]);

  const handleClear = useCallback(() => {
    pad.clear();
    setErrorKey(null);
  }, [pad]);

  return (
    <div className="signature-capture" role="main" aria-label={t('submitDocuments.signature.screenTitle')}>
      {entryWarningOpen && (
        <div
          className="signature-capture__warning-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="signature-entry-warning-title"
        >
          <div className="signature-capture__warning-box">
            <div id="signature-entry-warning-title" className="signature-capture__warning-body">
              <p className="signature-capture__warning-line">
                {t('submitDocuments.signature.entryWarning.line1')}
              </p>
              <p className="signature-capture__warning-line">
                {t('submitDocuments.signature.entryWarning.line2')}
              </p>
            </div>
            <button
              type="button"
              className="signature-capture__warning-ok"
              onClick={() => setEntryWarningOpen(false)}
            >
              {t('submitDocuments.signature.entryWarning.ok')}
            </button>
          </div>
        </div>
      )}

      <div className="signature-capture__phone">
        <div className="signature-capture__stage">
        <div className="signature-capture__content">
          <p className="signature-capture__instruction">{t('submitDocuments.signature.instruction')}</p>

          <div className="signature-capture__pad-block">
            <div className="signature-capture__pad">
              <div className="signature-capture__pad-divider" aria-hidden />
              <canvas
                ref={pad.canvasRef}
                className="signature-capture__canvas"
                aria-label={t('submitDocuments.signature.padLabel')}
                onPointerDown={pad.handlePointerDown}
                onPointerMove={pad.handlePointerMove}
                onPointerUp={pad.handlePointerUp}
                onPointerCancel={pad.handlePointerCancel}
                onPointerLeave={pad.handlePointerUp}
              />
            </div>
          </div>

          {errorKey && (
            <p className="signature-capture__error" role="alert">
              {t(
                errorKey === 'empty'
                  ? 'submitDocuments.signature.emptyError'
                  : 'submitDocuments.signature.uploadError'
              )}
            </p>
          )}
        </div>

        <div className="signature-capture__toolbar">
          <div className="signature-capture__tools">
            <button
              type="button"
              className={
                pad.mode === 'erase'
                  ? 'signature-capture__tool-btn signature-capture__tool-btn--active'
                  : 'signature-capture__tool-btn'
              }
              onClick={() => pad.setMode('erase')}
              aria-label={t('submitDocuments.signature.eraseTool')}
              aria-pressed={pad.mode === 'erase'}
            >
              <img src={eraserIcon} alt="" className="signature-capture__tool-icon" />
            </button>
            <button
              type="button"
              className={
                pad.mode === 'draw'
                  ? 'signature-capture__tool-btn signature-capture__tool-btn--active'
                  : 'signature-capture__tool-btn'
              }
              onClick={() => pad.setMode('draw')}
              aria-label={t('submitDocuments.signature.drawTool')}
              aria-pressed={pad.mode === 'draw'}
            >
              <img src={editIcon} alt="" className="signature-capture__tool-icon" />
            </button>
          </div>

          <div className="signature-capture__actions">
            <button
              type="button"
              className="signature-capture__btn signature-capture__btn--clear"
              onClick={handleClear}
              disabled={submitting}
            >
              {t('submitDocuments.signature.clear')}
            </button>
            <button
              type="button"
              className="signature-capture__btn signature-capture__btn--confirm"
              onClick={() => void handleConfirm()}
              disabled={submitting}
            >
              {t('submitDocuments.signature.confirm')}
            </button>
            <button
              type="button"
              className="signature-capture__btn signature-capture__btn--back"
              onClick={onBack}
              disabled={submitting}
            >
              {t('submitDocuments.signature.back')}
            </button>
          </div>
        </div>
        </div>

        <footer className="signature-capture__copyright" aria-label="Copyright">
          <p>{COPYRIGHT}</p>
        </footer>
      </div>
    </div>
  );
};

export default SignatureCaptureScreen;
